/**
 * src/components/RatingPopup.js
 *
 * Timed rating popup appears after scheduled delay (24hr after 10min activity).
 * Shows star rating + optional feedback, then submits to adminService/feedbackService.
 * Also handles admin-sent targeted popups.
 */

import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  X,
  Star,
  Send,
  Loader,
  CheckCircle,
  Sparkles,
  MessageSquareText,
  Bell,
  ExternalLink,
} from "lucide-react";
import { submitFeedback } from "../services/feedbackService";
import { dismissPopup } from "../services/feedbackService";
import { errorDev } from '../utils/logger.js';

/**
 * RatingPopup renders based on popup.type:
 *  - "feedback_rating": Star rating + feedback message
 *  - "admin_message": Admin-sent targeted message with optional action button
 */
export default function RatingPopup({ popup, onDismiss, user }) {
  const { t, ready } = useTranslation();
  const navigate = useNavigate();
  const safeT = (key, fallback) => {
    if (!ready) return fallback;
    try { const r = t(key); return r === key ? fallback : r; } catch (err) { errorDev("Operation failed:", err);  return fallback; }
  };

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const handleDismiss = useCallback(async () => {
    try {
      if (popup?.id) await dismissPopup(popup.id);
    } catch (err) {
      // Silent
    errorDev("Operation failed:", err);
    }
    onDismiss?.();
  }, [popup, onDismiss]);

  const handleSubmitRating = useCallback(async () => {
    if (!rating && !message.trim()) return;
    setSubmitting(true);
    try {
      await submitFeedback(user, {
        category: "general",
        message: message.trim() || `Rated ${rating}/5 stars`,
        rating: rating || null,
        source: "timed",
        metadata: { popupId: popup?.id, page: window.location.pathname },
      });
      setSuccess(true);
      setTimeout(async () => {
        try { if (popup?.id) await dismissPopup(popup.id); } catch (err) { errorDev("Operation failed:", err);}
        onDismiss?.();
      }, 2000);
    } catch (err) {
      // Still dismiss on error, don't block user
      errorDev("Operation failed:", err);
      handleDismiss();
    } finally {
      setSubmitting(false);
    }
  }, [rating, message, popup, onDismiss, handleDismiss]);

  const handleAction = useCallback(() => {
    if (popup?.actionUrl) {
      if (popup.actionUrl.startsWith("/")) {
        navigate(popup.actionUrl);
      } else {
        window.open(popup.actionUrl, "_blank", "noopener");
      }
    }
    handleDismiss();
  }, [popup, navigate, handleDismiss]);

  if (!popup) return null;

  const activeRating = hoverRating || rating;
  const isAdminMessage = popup.type === "admin_message";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={handleDismiss} />

      {/* Popup Card */}
      <div className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
        {success ? (
          /* ─── Success ─── */
          <div className="p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
              <CheckCircle className="w-7 h-7 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white">
              {safeT("feedback.thankYou", "Thank you!")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {safeT("feedback.ratingReceived", "Your feedback helps us improve Orrith - Cycle of Certainty.")}
            </p>
          </div>
        ) : isAdminMessage ? (
          /* ─── Admin Message Popup ─── */
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                  Orrith - Cycle of Certainty
                </span>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-400 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
              {popup.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {popup.message}
            </p>
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={handleDismiss}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 rounded-xl transition"
              >
                {safeT("common:dismiss", "Dismiss")}
              </button>
              {popup.actionLabel && popup.actionUrl && (
                <button
                  onClick={handleAction}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl transition"
                >
                  {popup.actionLabel}
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ) : (
          /* ─── Rating Popup ─── */
          <div className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                  {safeT("feedback.quickRating", "Quick Rating")}
                </span>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-400 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
              {popup.title || safeT("feedback.rateTitle", "Rate your experience")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {popup.message || safeT("feedback.rateSubtitle", "How has your Orrith - Cycle of Certainty experience been?")}
            </p>

            {/* Stars */}
            <div className="flex items-center justify-center gap-1.5 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-0.5 transition-transform hover:scale-125"
                >
                  <Star
                    className={`w-8 h-8 transition ${
                      star <= activeRating
                        ? "fill-amber-400 text-amber-400"
                        : "text-gray-300 dark:text-neutral-600"
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Expandable feedback */}
            {rating > 0 && !showFeedback && (
              <button
                onClick={() => setShowFeedback(true)}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-green-600 dark:text-green-400 hover:underline mb-3"
              >
                <MessageSquareText className="w-3.5 h-3.5" />
                {safeT("feedback.addComment", "Add a comment (optional)")}
              </button>
            )}

            {showFeedback && (
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={safeT("feedback.commentPlaceholder", "Any additional thoughts?")}
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition mb-3"
              />
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleDismiss}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-xl transition"
              >
                {safeT("common:notNow", "Not now")}
              </button>
              <button
                onClick={handleSubmitRating}
                disabled={submitting || !rating}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 rounded-xl transition disabled:opacity-50"
              >
                {submitting ? (
                  <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {safeT("feedback.submitRating", "Submit")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



